/**
 * The membership list, and the two mutations a tenant admin drives from it:
 * changing a member's role and revoking a membership outright. `GET
 * /tenants/{t}/members`, `PUT .../members/{m}/role`, `DELETE .../members/{m}`
 * exist as fixtures and handlers only (`../api/`) -- the real backend route
 * has not landed yet.
 *
 * Neither mutation predicts a refusal. The rule that a tenant keeps at
 * least one tenant-wide administrator is owned elsewhere; this file only
 * invalidates the list on success and lets `ApiFailureError` surface to the
 * caller otherwise.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiFailureError } from '../../../lib/http/errors';
import { useApiClient } from '../../session/client';
import { membershipRowSchema, type MembershipRow } from '../api/schemas';
import { GET_ROUTES, routePath } from '../../../lib/http/routes';

export const MEMBERS_QUERY_KEY = (tenantId: string) => ['tenant', tenantId, 'members'] as const;

export function useMembers(tenantId: string): UseQueryResult<MembershipRow[], ApiFailureError> {
  const client = useApiClient();

  return useQuery<MembershipRow[], ApiFailureError>({
    queryKey: MEMBERS_QUERY_KEY(tenantId),
    queryFn: async () => {
      const { items } = await client.getParsed(
        routePath(GET_ROUTES.tenantMembers, { tenantId }),
        GET_ROUTES.tenantMembers.schema,
      );
      return items;
    },
  });
}

export function useChangeRole(
  tenantId: string,
): UseMutationResult<MembershipRow, ApiFailureError, { membershipId: string; role: string }> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<MembershipRow, ApiFailureError, { membershipId: string; role: string }>({
    mutationFn: ({ membershipId, role }) =>
      client.putParsed(
        `/tenants/${tenantId}/members/${membershipId}/role`,
        { role },
        membershipRowSchema,
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MEMBERS_QUERY_KEY(tenantId) }),
  });
}

export function useRevokeMembership(
  tenantId: string,
): UseMutationResult<void, ApiFailureError, { membershipId: string }> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<void, ApiFailureError, { membershipId: string }>({
    mutationFn: ({ membershipId }) => client.del(`/tenants/${tenantId}/members/${membershipId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MEMBERS_QUERY_KEY(tenantId) }),
  });
}
